import { createContext, useContext, useState, useEffect, useCallback } from 'react'

const STORAGE_KEY = 'dojo-pro:sidebar-collapsed'
const MOBILE_BREAKPOINT = 768

interface SidebarContextValue {
  isCollapsed: boolean
  isMobile: boolean
  isMobileOpen: boolean
  toggleSidebar: () => void
  setCollapsed: (collapsed: boolean) => void
  openMobile: () => void
  closeMobile: () => void
}

export const SidebarContext = createContext<SidebarContextValue | null>(null)

export function useSidebar() {
  const context = useContext(SidebarContext)

  if (!context) {
    throw new Error('useSidebar must be used within a SidebarProvider')
  }

  return context
}

export function useSidebarState() {
  const [isCollapsed, setIsCollapsed] = useState(() => {
    if (typeof window === 'undefined') return false
    const stored = localStorage.getItem(STORAGE_KEY)

    return stored !== null ? JSON.parse(stored) : false
  })

  const [isMobile, setIsMobile] = useState(() => {
    if (typeof window === 'undefined') return false

    return window.innerWidth < MOBILE_BREAKPOINT
  })

  const [isMobileOpen, setIsMobileOpen] = useState(false)

  useEffect(() => {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(isCollapsed))
  }, [isCollapsed])

  useEffect(() => {
    const handleResize = () => {
      const mobile = window.innerWidth < MOBILE_BREAKPOINT

      setIsMobile(mobile)

      if (!mobile) {
        setIsMobileOpen(false)
      }
    }

    window.addEventListener('resize', handleResize)

    return () => window.removeEventListener('resize', handleResize)
  }, [])

  // Atalho de teclado Ctrl+B / Cmd+B
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if ((e.metaKey || e.ctrlKey) && e.key === 'b') {
        e.preventDefault()

        if (isMobile) {
          setIsMobileOpen((prev) => !prev)
        } else {
          setIsCollapsed((prev: boolean) => !prev)
        }
      }
    }

    window.addEventListener('keydown', handleKeyDown)

    return () => window.removeEventListener('keydown', handleKeyDown)
  }, [isMobile])

  const toggleSidebar = useCallback(() => {
    if (isMobile) {
      setIsMobileOpen((prev) => !prev)
    } else {
      setIsCollapsed((prev: boolean) => !prev)
    }
  }, [isMobile])

  const setCollapsed = useCallback((collapsed: boolean) => {
    setIsCollapsed(collapsed)
  }, [])

  const openMobile = useCallback(() => {
    setIsMobileOpen(true)
  }, [])

  const closeMobile = useCallback(() => {
    setIsMobileOpen(false)
  }, [])

  return {
    isCollapsed,
    isMobile,
    isMobileOpen,
    toggleSidebar,
    setCollapsed,
    openMobile,
    closeMobile,
  }
}
